import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  FileText,
  Sparkles,
  ChevronRight,
  Loader2,
  ArrowLeft,
  BrainCircuit,
  Stethoscope,
  Mail,
  MessageSquare,
  FileSignature,
} from "lucide-react";
import {
  ReferralLetterData,
  ReferralTemplate,
  useReferralLetterData,
} from "@/lib/settings/ReferralLetterData";
import { useTitanApp } from "@/hooks/useTitanApp";
import useTitanType from "@/hooks/useTitanType";
import { setShowReferralLetterDialog } from "@/dispatchers/patientDispatchers";
import { getEmailsAndSmsTemplates } from "@/dispatchers/templateDispatchers";

interface ReferralLetterDialogProps {
  trigger: React.ReactNode;
  onSave?: (data: any) => void;
  onLetterReady?: (
    title: string,
    content: string,
    subject?: string,
    documentType?: "email" | "letter",
  ) => void;
  transcriptionText?: string;
  externalOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const REFERRAL_STATE_KEY = "titanverse_referralModal";

function saveReferralState(title: string, content: string) {
  try {
    sessionStorage.setItem(
      REFERRAL_STATE_KEY,
      JSON.stringify({ title, content }),
    );
  } catch {}
}

function loadReferralState(): { title: string; content: string } | null {
  try {
    const saved = sessionStorage.getItem(REFERRAL_STATE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
}

function clearReferralState() {
  try {
    sessionStorage.removeItem(REFERRAL_STATE_KEY);
  } catch {}
}

export function ReferralLetterDialog({
  trigger,
  onSave,
  onLetterReady,
  transcriptionText,
  externalOpen,
  onOpenChange,
}: ReferralLetterDialogProps) {
  const { templateState } = useTitanApp();
  const { typeState } = useTitanType();
  const {
    companyId,
    currentUser,
    currentPharmacy,
    selectedPatientDetails,
    showReferralLetterDialog,
    applyTemplatePlaceholders,
  } = useReferralLetterData();
  const { emailsAndSmsTemplates } = templateState || {};
  const { messageTemplates } = typeState || {};
  const [internalOpen, setInternalOpen] = useState(false);
  const open =
    showReferralLetterDialog !== undefined
      ? showReferralLetterDialog
      : internalOpen;
  const setOpen = (value: boolean) => {
    setInternalOpen(value);
    onOpenChange?.(value);
  };
  const [view, setView] = useState<"selection" | "prompt">("selection");
  const [templateFilter, setTemplateFilter] = useState<
    "all" | "email" | "letter"
  >("email");
  const [customPrompt, setCustomPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [templates, setTemplates] = useState<ReferralTemplate[]>([]);

  const hasTranscription = !!transcriptionText?.trim();

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setShowReferralLetterDialog(false);
      setView("selection");
      setCustomPrompt("");
      setIsGenerating(false);
      setIsGeneratingAi(false);
    }
  };
  const handleSelectTemplate = (template: ReferralTemplate) => {
    const today = ReferralLetterData.formatDate();
    const content = applyTemplatePlaceholders(template.content, today);

    const title = template.name;
    const subject = template.subject || "";

    saveReferralState(title, content);
    handleOpenChange(false);
    onLetterReady?.(
      title,
      content,
      subject,
      ReferralLetterData.toDocumentType(template.type),
    );
  };

  const handleGenerateFromTranscription = async () => {
    if (!transcriptionText?.trim()) return;

    setIsGeneratingAi(true);

    // Simulate AI processing delay
    await new Promise((resolve) => setTimeout(resolve, 2500));
  };

  const handleGenerateFromPrompt = async () => {
    if (!customPrompt.trim()) return;

    setIsGenerating(true);

    try {
      const patientName =
        selectedPatientDetails?.patientName ||
        `${selectedPatientDetails?.foreName || ""} ${
          selectedPatientDetails?.surName || ""
        }`.trim();
      const dobRaw = selectedPatientDetails?.dob;
      const dob = dobRaw
        ? new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          }).format(new Date(dobRaw))
        : "";

      const { title, content } = await ReferralLetterData.generateLetterFromApi(
        {
          prompt: customPrompt.trim(),
          patientName: patientName || undefined,
          dob: dob || undefined,
          clinicianName: currentUser?.fullName || undefined,
        },
      );

      const gpName =
  selectedPatientDetails?.surgery?.generalPractitioner?.name?.trim();

const gpNameSwappedContent =
  gpName && gpName.length > 0
    ? content.replace(/\[GP_NAME_PLACEHOLDER\]/g, gpName)
    : content;
    
      saveReferralState(title, gpNameSwappedContent);
      setIsGenerating(false);
      handleOpenChange(false);
      onLetterReady?.(title, gpNameSwappedContent, title, "letter");
    } catch (error) {
      console.error("Failed to generate referral letter", error);
      setIsGenerating(false);
    }
  };

  // Get a short preview of what the AI detected from the transcription
  const getTranscriptionPreview = (): string =>
    ReferralLetterData.getTranscriptionPreview(transcriptionText || "");

  useEffect(() => {
    if (open && companyId) {
      getEmailsAndSmsTemplates(companyId);
    }
  }, [open, companyId]);

  useEffect(() => {
    const combinedTemplates = [
      ...(Array.isArray(messageTemplates) ? messageTemplates : []),
      ...(Array.isArray(emailsAndSmsTemplates) ? emailsAndSmsTemplates : []),
    ];

    if (combinedTemplates.length > 0) {
      const mapped = ReferralLetterData.mapMessageTemplates(combinedTemplates);
      const uniqueById = Array.from(
        new Map(mapped.map((template) => [template.id, template])).values(),
      );
      setTemplates(uniqueById);
    } else {
      setTemplates([]);
    }
  }, [messageTemplates, emailsAndSmsTemplates]);

  const visibleTemplates = templates.filter((template) => {
    if (template.type === "sms") {
      return false;
    }

    if (templateFilter === "all") {
      return true;
    }

    if (templateFilter === "email") {
      return template.type === "email" || template.type === "both";
    }

    return template.type === "letter";
  });

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[60vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {view === "prompt" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setView("selection")}
                  className="h-auto p-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <DialogTitle className="text-lg font-semibold">
                {view === "selection"
                  ? "Document Templates"
                  : "Generate from Prompt"}
              </DialogTitle>
            </div>
          </DialogHeader>

          {view === "selection" && (
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="space-y-2">
                {/* AI Generated section */}
                {hasTranscription && (
                  <>
                    <p className="px-1 text-xs font-medium tracking-wide text-gray-400 uppercase">
                      AI Generated
                    </p>

                    <button
                      onClick={handleGenerateFromTranscription}
                      disabled={isGeneratingAi}
                      className="flex items-center w-full gap-3 p-3 text-left transition-colors border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 group disabled:opacity-60 disabled:cursor-wait"
                    >
                      <div className="flex items-center justify-center flex-shrink-0 transition-colors bg-gray-100 rounded-md w-9 h-9 group-hover:bg-blue-100">
                        {isGeneratingAi ? (
                          <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {isGeneratingAi
                            ? "Generating..."
                            : "Suggested from Consultation"}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {getTranscriptionPreview()}
                        </p>
                      </div>
                      {!isGeneratingAi && (
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400" />
                      )}
                    </button>

                    <button
                      onClick={() => setView("prompt")}
                      className="flex items-center w-full gap-3 p-3 text-left transition-colors border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 group"
                    >
                      <div className="flex items-center justify-center flex-shrink-0 transition-colors bg-gray-100 rounded-md w-9 h-9 group-hover:bg-blue-100">
                        <Sparkles className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          Generate with Custom Prompt
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Describe what you need and we'll draft it
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400" />
                    </button>
                  </>
                )}

                {!hasTranscription && (
                  <button
                    onClick={() => setView("prompt")}
                    className="flex items-center w-full gap-3 p-3 text-left transition-colors border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 group"
                  >
                    <div className="flex items-center justify-center flex-shrink-0 transition-colors bg-gray-100 rounded-md w-9 h-9 group-hover:bg-blue-100">
                      <Sparkles className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Generate with Custom Prompt
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Describe what you need and we'll draft it
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400" />
                  </button>
                )}

                {/* Templates section - only show if templates exist */}
                {templates.length > 0 && (
                  <>
                    <div className="relative py-2">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="px-3 text-xs text-gray-400 uppercase bg-white">
                          Templates
                        </span>
                      </div>
                    </div>

                    {/* Type filter */}
                    <div className="flex items-center gap-1.5">
                      {(["all", "email", "letter"] as const).map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setTemplateFilter(filter)}
                          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                            templateFilter === filter
                              ? "bg-slate-900 text-white"
                              : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                          }`}
                        >
                          {filter === "all"
                            ? "All"
                            : filter === "letter"
                              ? "Letter"
                              : filter.toUpperCase()}
                        </button>
                      ))}
                    </div>

                    {/* Template list from Settings */}
                    {visibleTemplates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          className="flex items-center w-full gap-3 p-3 text-left transition-colors border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 group"
                        >
                          <div className="flex items-center justify-center flex-shrink-0 transition-colors bg-gray-100 rounded-md w-9 h-9 group-hover:bg-blue-100">
                            {template.type === "sms" ? (
                              <MessageSquare className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                            ) : template.type === "letter" ? (
                              <FileSignature className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                            ) : (
                              <Mail className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {template.name}
                            </p>
                          </div>
                          <span
                            className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                              template.type === "sms"
                                ? "bg-green-100 text-green-700"
                                : template.type === "letter"
                                  ? "bg-purple-100 text-purple-700"
                                  : template.type === "both"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {template.type === "both"
                              ? "Email/SMS"
                              : template.type === "letter"
                                ? "Letter"
                                : template.type.toUpperCase()}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400" />
                        </button>
                      ))}
                  </>
                )}
              </div>
            </div>
          )}

          {view === "prompt" && (
            <div className="flex-1 p-6 space-y-4 overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="prompt" className="text-sm font-medium">
                  Describe the referral letter you need
                </Label>
                <Textarea
                  id="prompt"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="e.g. Write a referral letter to the ENT department for a patient presenting with chronic sinusitis unresponsive to medical therapy, requiring surgical evaluation..."
                  className="min-h-[160px] resize-none"
                />
                <p className="text-xs text-gray-500">
                  Be as specific as possible about the clinical context,
                  specialty, and reason for referral. You can edit the generated
                  letter afterwards.
                </p>
              </div>

              <Button
                onClick={handleGenerateFromPrompt}
                disabled={!customPrompt.trim() || isGenerating}
                className="w-full text-white bg-black hover:bg-gray-800"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Letter...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Letter
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
